import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { 
  H1, 
  H2, 
  H3, 
  Body, 
  Card, 
  Button, 
  Badge, 
  ThemeToggle, 
  ThemeCustomizer 
} from '../components/ui';
import { PaintBrushIcon, SwatchIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';

const ThemeDemo: React.FC = () => {
  const { theme, isDarkMode } = useTheme();
  const [customizerOpen, setCustomizerOpen] = useState(false);

  const colorCategories = [
    { name: 'Primary', colors: theme.colors.primary, description: 'Main brand colors' },
    { name: 'Secondary', colors: theme.colors.secondary, description: 'Secondary brand colors' },
    { name: 'Accent', colors: theme.colors.accent, description: 'Accent colors for highlights' },
    { name: 'Neutral', colors: theme.colors.neutral, description: 'Neutral colors for text and backgrounds' },
  ];

  const buttonVariants = ['primary', 'secondary', 'ghost', 'outline'] as const;
  const badgeVariants = ['default', 'primary', 'secondary', 'success', 'warning', 'error'] as const;

  const renderColorPalette = (name: string, colors: Record<string, string>, description: string) => (
    <Card key={name} className="p-6">
      <div className="mb-4">
        <H3 className="mb-1">{name}</H3>
        <Body className="text-sm text-gray-600 dark:text-gray-400">{description}</Body>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {Object.entries(colors).map(([shade, color]) => (
          <motion.div
            key={shade}
            className="group relative"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div
              className="aspect-square rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-sm cursor-pointer"
              style={{ backgroundColor: color }}
              title={`${name} ${shade}: ${color}`}
            />
            <div className="text-xs text-center mt-1 font-mono text-gray-600 dark:text-gray-400">
              {shade}
            </div>
          </motion.div>
        ))}
      </div>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <H1 className="flex items-center justify-center space-x-3">
            <SwatchIcon className="w-8 h-8 text-primary-600" />
            <span>Edna Design System</span>
          </H1>
          <Body className="text-xl text-gray-600 dark:text-gray-400 mt-2">
            Theme showcase and customization demo
          </Body>
        </motion.div>
        
        <div className="flex items-center justify-center space-x-4">
          <ThemeToggle variant="dropdown" />
          <Button
            variant="primary"
            onClick={() => setCustomizerOpen(true)}
            className="flex items-center space-x-2"
          >
            <PaintBrushIcon className="w-4 h-4" />
            <span>Customize Theme</span>
          </Button>
        </div>
      </div>

      {/* Theme Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                {isDarkMode ? (
                  <MoonIcon className="w-8 h-8 text-primary-600" />
                ) : (
                  <SunIcon className="w-8 h-8 text-primary-600" />
                )}
              </div>
              <H3>Current Theme</H3>
              <Body className="text-sm text-gray-600 dark:text-gray-400">{theme.name}</Body>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-secondary-100 dark:bg-secondary-900 rounded-full flex items-center justify-center">
                <SwatchIcon className="w-8 h-8 text-secondary-600" />
              </div>
              <H3>Color Palette</H3>
              <Body className="text-sm text-gray-600 dark:text-gray-400">
                {Object.keys(theme.colors).length} color categories
              </Body>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-accent-100 dark:bg-accent-900 rounded-full flex items-center justify-center">
                <PaintBrushIcon className="w-8 h-8 text-accent-600" />
              </div>
              <H3>Customizable</H3>
              <Body className="text-sm text-gray-600 dark:text-gray-400">
                Full theme customization
              </Body>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Color Palettes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="mb-6">
          <H2>Color Palettes</H2>
          <Body className="text-gray-600 dark:text-gray-400 mt-2">
            The Edna design system uses a comprehensive color palette with semantic meanings.
          </Body>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {colorCategories.map(category => 
            renderColorPalette(category.name, category.colors, category.description)
          )}
        </div>
      </motion.div>

      {/* Semantic Colors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <Card className="p-6">
          <H3 className="mb-4">Semantic Colors</H3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(theme.colors.semantic).map(([name, color]) => (
              <div key={name} className="flex items-center space-x-3">
                <div
                  className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600"
                  style={{ backgroundColor: color }}
                />
                <div>
                  <Body className="font-medium capitalize">{name}</Body>
                  <Body className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {color}
                  </Body>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Component Examples */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="mb-6">
          <H2>Component Examples</H2>
          <Body className="text-gray-600 dark:text-gray-400 mt-2">
            See how the theme system works with various UI components.
          </Body>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Buttons */}
          <Card className="p-6">
            <H3 className="mb-4">Buttons</H3>
            <div className="space-y-3">
              {buttonVariants.map(variant => (
                <Button key={variant} variant={variant} size="md" className="mr-3">
                  {variant.charAt(0).toUpperCase() + variant.slice(1)} Button
                </Button>
              ))}
            </div>
          </Card>
          
          {/* Badges */}
          <Card className="p-6">
            <H3 className="mb-4">Badges</H3>
            <div className="flex flex-wrap gap-2">
              {badgeVariants.map(variant => (
                <Badge key={variant} variant={variant}>
                  {variant.charAt(0).toUpperCase() + variant.slice(1)}
                </Badge>
              ))}
            </div>
          </Card>
        </div>
      </motion.div>

      {/* Typography */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <Card className="p-6">
          <H3 className="mb-4">Typography</H3>
          <div className="space-y-4">
            <div>
              <H1>Heading 1 - Display Title</H1>
              <Body className="text-sm text-gray-500 dark:text-gray-400">
                Font: {theme.typography.fontFamily.display}
              </Body>
            </div>
            <div>
              <H2>Heading 2 - Section Title</H2>
              <Body className="text-sm text-gray-500 dark:text-gray-400">
                Font: {theme.typography.fontFamily.display}
              </Body>
            </div>
            <div>
              <H3>Heading 3 - Subsection Title</H3>
              <Body className="text-sm text-gray-500 dark:text-gray-400">
                Font: {theme.typography.fontFamily.display}
              </Body>
            </div>
            <div>
              <Body>
                Body text - This is the main text used throughout the application. 
                It's designed to be highly readable and accessible.
              </Body>
              <Body className="text-sm text-gray-500 dark:text-gray-400">
                Font: {theme.typography.fontFamily.sans}
              </Body>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Spacing and Layout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <Card className="p-6">
          <H3 className="mb-4">Spacing Scale</H3>
          <div className="space-y-3">
            {Object.entries(theme.spacing).map(([size, value]) => (
              <div key={size} className="flex items-center space-x-4">
                <div className="w-12 text-sm font-mono text-gray-600 dark:text-gray-400">
                  {size}
                </div>
                <div
                  className="bg-primary-200 dark:bg-primary-800 h-4 rounded"
                  style={{ width: value }}
                />
                <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  {value}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Theme Customizer */}
      <ThemeCustomizer
        isOpen={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
      />
    </div>
  );
};

export default ThemeDemo;
